// Venue-timezone-aware time helpers.
//
// All show-related timestamps are stored as UTC TIMESTAMPTZ (cloud PG) or
// ISO-8601 strings; they MUST be rendered in the venue's IANA timezone
// (venues.timezone), not the operator's browser local. Doors, curfew,
// actual_show_start_ts, drill-down chart axes — every show-related time
// display goes through these helpers so a touring crew member in PT
// looking at a venue in ET sees the venue's local wall-clock, not their own.
//
// When venue.timezone is null (operator hasn't set it yet), we fall back to
// UTC and tag the output — a visible signal that the missing data is the
// reason, not a quiet browser-local format pretending the number is right.
//
// Extracted from ShowWindowSection.tsx so every surface can share one
// source of truth for venue-time conversion.

/** Parts of an instant as observed in tz. */
export function partsInTz(iso: string, tz: string): Record<string, string> {
  const d = new Date(iso);
  const fmt = new Intl.DateTimeFormat("en-US", {
    timeZone: tz,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
  const out: Record<string, string> = {};
  for (const p of fmt.formatToParts(d)) {
    if (p.type !== "literal") out[p.type] = p.value;
  }
  if (out.hour === "24") out.hour = "00";
  return out;
}

/** ISO instant -> "YYYY-MM-DDTHH:MM" for a datetime-local input, in venue tz. */
export function isoToLocalInput(iso: string, tz: string): string {
  const p = partsInTz(iso, tz);
  return `${p.year}-${p.month}-${p.day}T${p.hour}:${p.minute}`;
}

/** ISO instant -> "HH:MM" wall-clock label in venue tz. */
export function isoToClock(iso: string, tz: string): string {
  const p = partsInTz(iso, tz);
  return `${p.hour}:${p.minute}`;
}

/** ISO instant -> "YYYY-MM-DD HH:MM" label in venue tz. */
export function isoToDateClock(iso: string, tz: string): string {
  const p = partsInTz(iso, tz);
  return `${p.year}-${p.month}-${p.day} ${p.hour}:${p.minute}`;
}

/** Offset of tz from UTC at the given instant (ms; positive ahead of UTC). */
export function tzOffsetMs(at: Date, tz: string): number {
  const local = partsInTz(at.toISOString(), tz);
  const asUtc = Date.UTC(
    Number(local.year),
    Number(local.month) - 1,
    Number(local.day),
    Number(local.hour),
    Number(local.minute),
  );
  const flooredReal = Math.floor(at.getTime() / 60000) * 60000;
  return asUtc - flooredReal;
}

/** "YYYY-MM-DDTHH:MM" venue-local wall-clock -> ISO-8601 UTC instant.
 *  Returns null when the input is malformed. */
export function localInputToIso(value: string, tz: string): string | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/.exec(value.trim());
  if (!m) return null;
  const [, y, mo, d, h, mi] = m;
  const guessUtc = Date.UTC(+y, +mo - 1, +d, +h, +mi);
  const offset = tzOffsetMs(new Date(guessUtc), tz);
  return new Date(guessUtc - offset).toISOString();
}

/** "YYYY-MM-DD" date + "HH:MM" time -> ISO-8601 UTC instant, in venue tz. */
export function dateAndTimeToIso(day: string, time: string, tz: string): string | null {
  const dm = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day.trim());
  const tm = /^(\d{2}):(\d{2})$/.exec(time.trim());
  if (!dm || !tm) return null;
  return localInputToIso(`${day}T${time}`, tz);
}

// ---------------------------------------------------------------------------
// Display helpers - UTC fallback with visible badge when venueTz is missing
// ---------------------------------------------------------------------------

export interface VenueTimeFormatted {
  text: string;
  is_utc_fallback: boolean;
}

/** Format an ISO instant in the venue's IANA timezone, or fall back to UTC
 *  with is_utc_fallback=true when the zone is missing. */
export function formatVenueLocal(
  iso: string | number | Date,
  venueTz: string | null | undefined,
  opts: { include_date?: boolean } = {},
): VenueTimeFormatted {
  const asIso = typeof iso === "string"
    ? iso
    : (iso instanceof Date ? iso.toISOString() : new Date(iso).toISOString());
  const tz = (venueTz && venueTz.trim()) ? venueTz : "UTC";
  const p = partsInTz(asIso, tz);
  const includeDate = opts.include_date ?? true;
  const text = includeDate
    ? `${p.year}-${p.month}-${p.day} ${p.hour}:${p.minute}`
    : `${p.hour}:${p.minute}`;
  return { text, is_utc_fallback: !venueTz };
}

/** Convenience wrapper that returns a single string with a "(UTC)" suffix
 *  when fallback is in play. Use for inline labels where a separate badge
 *  component would be visual overkill. */
export function formatVenueLocalInline(
  iso: string | number | Date,
  venueTz: string | null | undefined,
  opts: { include_date?: boolean } = {},
): string {
  const r = formatVenueLocal(iso, venueTz, opts);
  return r.is_utc_fallback ? `${r.text} (UTC)` : r.text;
}
